import { useEffect } from 'react'
import { SITE, absoluteUrl } from './siteConfig'
import {
  organizationSchema,
  personSchema,
  websiteSchema,
  webPageSchema,
  breadcrumbSchema,
  pruneSchema,
} from './schema'

export interface SeoProps {
  /** Page title without the site-name suffix. Keep under ~55 characters. */
  title: string
  /** Meta description. Aim for 140–160 characters. */
  description: string
  /** Absolute URL of the share/preview image (1200x630 recommended). */
  image?: string
  /** Route path, e.g. "/journal/on-life". Drives canonical + og:url. */
  path?: string
  /** Open Graph object type. */
  type?: 'website' | 'article' | 'profile'
  /** Breadcrumb trail. Home is prepended automatically. */
  breadcrumbs?: Array<{ name: string; path: string }>
  /** Page-specific JSON-LD entities appended to the @graph. */
  schema?: Array<Record<string, unknown>>
  /** Set true for thin/utility pages that should stay out of the index. */
  noIndex?: boolean
}

const MANAGED_ATTR = 'data-seo-managed'
const SCHEMA_ID = 'structured-data'

function upsertMeta(
  selector: string,
  attrs: Record<string, string>,
  content: string,
) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    Object.entries(attrs).forEach(([key, value]) =>
      element!.setAttribute(key, value),
    )
    element.setAttribute(MANAGED_ATTR, 'true')
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(
    `link[rel="${rel}"]`,
  )
  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    element.setAttribute(MANAGED_ATTR, 'true')
    document.head.appendChild(element)
  }
  element.setAttribute('href', href)
}

/**
 * Centralised head management: title, description, canonical, Open Graph,
 * Twitter cards, robots directives and JSON-LD structured data.
 *
 * WORDPRESS PORT: every value here maps to a Yoast/RankMath field or an ACF
 * `seo_*` field group on the corresponding page/CPT.
 */
export function useSeo({
  title,
  description,
  image,
  path = '/',
  type = 'website',
  breadcrumbs,
  schema,
  noIndex = false,
}: SeoProps) {
  const serialisedSchema = schema ? JSON.stringify(schema) : ''
  const serialisedCrumbs = breadcrumbs ? JSON.stringify(breadcrumbs) : ''

  useEffect(() => {
    const fullTitle = title.includes(SITE.shortName)
      ? title
      : `${title} — ${SITE.name}`
    const canonical = absoluteUrl(path)
    const shareImage = image ?? SITE.defaultImage

    document.title = fullTitle
    document.documentElement.lang = SITE.language

    // --- Core ---------------------------------------------------------
    upsertMeta('meta[name="description"]', { name: 'description' }, description)
    upsertMeta(
      'meta[name="robots"]',
      { name: 'robots' },
      noIndex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    )
    upsertMeta('meta[name="author"]', { name: 'author' }, SITE.name)

    // --- Open Graph ---------------------------------------------------
    const og: Array<[string, string]> = [
      ['og:title', fullTitle],
      ['og:description', description],
      ['og:url', canonical],
      ['og:type', type],
      ['og:image', shareImage],
      ['og:image:alt', title],
      ['og:site_name', SITE.name],
      ['og:locale', SITE.locale],
    ]
    og.forEach(([property, content]) =>
      upsertMeta(
        `meta[property="${property}"]`,
        { property },
        content,
      ),
    )

    // --- Twitter ------------------------------------------------------
    const twitter: Array<[string, string]> = [
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', fullTitle],
      ['twitter:description', description],
      ['twitter:image', shareImage],
    ]
    twitter.forEach(([name, content]) =>
      upsertMeta(`meta[name="${name}"]`, { name }, content),
    )

    // --- Canonical ----------------------------------------------------
    upsertLink('canonical', canonical)

    // --- Structured data (JSON-LD @graph) -----------------------------
    const trail = [
      { name: 'Home', path: '/' },
      ...(breadcrumbs ?? []),
    ].filter(
      (crumb, index, all) =>
        all.findIndex((item) => item.path === crumb.path) === index,
    )

    const graph = [
      organizationSchema(),
      personSchema(),
      websiteSchema(),
      webPageSchema({ path, title: fullTitle, description, image: shareImage }),
      ...(trail.length > 1 ? [breadcrumbSchema(trail)] : []),
      ...(schema ?? []),
    ]

    let scriptEl = document.getElementById(
      SCHEMA_ID,
    ) as HTMLScriptElement | null
    if (!scriptEl) {
      scriptEl = document.createElement('script')
      scriptEl.type = 'application/ld+json'
      scriptEl.id = SCHEMA_ID
      document.head.appendChild(scriptEl)
    }
    scriptEl.textContent = JSON.stringify(
      pruneSchema({ '@context': 'https://schema.org', '@graph': graph }),
    )
  }, [
    title,
    description,
    image,
    path,
    type,
    noIndex,
    serialisedSchema,
    serialisedCrumbs,
  ])
}
